(function () {
  if (window.DdYmeFitment) return;

  function parseCsvAttribute(value) {
    if (!value) return [];
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  function normalizeValue(value) {
    return String(value || '').trim().toLowerCase();
  }

  function formatEngineForMatch(engine) {
    return window.DdVehicleStorage?.formatEngineDisplay(engine) || String(engine || '').replace(/-/g, '.');
  }

  function resolveVehicleMakes(vehicle) {
    if (!vehicle?.make) return [];

    if (vehicle.make === 'ram') {
      if (vehicle.engine === '3-0L') {
        return ['Ram EcoDiesel', 'Dodge Ram'];
      }

      return ['Dodge Ram'];
    }

    const makeMap = {
      ford: 'Ford',
      gm: 'GM',
      jeep: 'Jeep',
      benz: 'Mercedes Benz',
      nissan: 'Nissan',
    };

    return [makeMap[vehicle.make] || vehicle.make];
  }

  function normalizeProductBody(body) {
    const normalized = normalizeValue(body);

    if (normalized.includes('pickup') || normalized === 'dual rear wheels') {
      return 'pickup';
    }

    if (
      normalized === 'cc' ||
      normalized === 'c&c' ||
      (normalized.includes('cab') && normalized.includes('chassis'))
    ) {
      return 'cc';
    }

    if (normalized === 'van') {
      return 'van';
    }

    return normalized;
  }

  function normalizeVehicleBody(body) {
    if (!body || body === 'all') return null;

    const bodyMap = {
      pickup: 'pickup',
      CC: 'cc',
      van: 'van',
    };

    return bodyMap[body] || normalizeValue(body);
  }

  function productMatchesBody(productBodies, vehicleBody) {
    const vehicleBodyKey = normalizeVehicleBody(vehicleBody);

    if (!vehicleBodyKey) return true;
    if (productBodies.length === 0) return true;

    return productBodies.some((body) => normalizeProductBody(body) === vehicleBodyKey);
  }

  function parseYearRange(value) {
    const trimmed = String(value || '').trim();
    const rangeMatch = trimmed.match(/^(\d{4})\s*[-–]\s*(\d{4})$/);

    if (!rangeMatch) return null;

    return {
      start: Number(rangeMatch[1]),
      end: Number(rangeMatch[2]),
    };
  }

  function yearMatchesVehicle(years, vehicleYear) {
    if (years.length === 0) return true;

    const year = Number(vehicleYear);
    if (!year) return false;

    return years.some((entry) => {
      const trimmed = String(entry).trim();
      if (trimmed === String(vehicleYear)) return true;

      const range = parseYearRange(trimmed);
      if (range) {
        return year >= range.start && year <= range.end;
      }

      return false;
    });
  }

  function normalizeDisplacement(value) {
    return normalizeValue(value).replace(/\s+/g, ' ');
  }

  function displacementMatchesVehicle(displacements, vehicleEngine) {
    if (displacements.length === 0) return true;

    const engineValue = normalizeDisplacement(formatEngineForMatch(vehicleEngine));
    if (!engineValue) return false;

    return displacements.some((displacement) => {
      const productDisplacement = normalizeDisplacement(displacement);

      if (productDisplacement === 'universal') return true;
      if (productDisplacement === engineValue) return true;

      if (engineValue === '3.0l') {
        return productDisplacement === '1500 3.0l' || productDisplacement.endsWith('3.0l');
      }

      return false;
    });
  }

  function productHasFitmentData(element) {
    return (
      parseCsvAttribute(element.dataset.ymeYears).length > 0 ||
      parseCsvAttribute(element.dataset.ymeMakes).length > 0 ||
      parseCsvAttribute(element.dataset.ymeDisplacements).length > 0
    );
  }

  function productMatchesVehicle(element, vehicle) {
    return productMatchesVehicleDebug(element, vehicle).match;
  }

  function productMatchesVehicleDebug(element, vehicle) {
    const years = parseCsvAttribute(element.dataset.ymeYears);
    const makes = parseCsvAttribute(element.dataset.ymeMakes);
    const displacements = parseCsvAttribute(element.dataset.ymeDisplacements);
    const bodies = parseCsvAttribute(element.dataset.ymeBodies);
    const vehicleMakes = resolveVehicleMakes(vehicle).map(normalizeValue);
    const hasFitmentData = productHasFitmentData(element);

    const yearMatch = yearMatchesVehicle(years, vehicle.year);
    const makeMatch =
      makes.length === 0 || makes.some((make) => vehicleMakes.includes(normalizeValue(make)));
    const displacementMatch = displacementMatchesVehicle(displacements, vehicle.engine);
    const bodyMatch = productMatchesBody(bodies, vehicle.body);

    const match = hasFitmentData && yearMatch && makeMatch && displacementMatch && bodyMatch;

    return {
      match,
      title: element.dataset.productTitle || '(untitled)',
      productType: element.dataset.productType || '',
      hasFitmentData,
      checks: {
        year: yearMatch,
        make: makeMatch,
        engine: displacementMatch,
        body: bodyMatch,
      },
      productFitment: {
        years,
        makes,
        engines: displacements,
        bodies,
      },
      vehicleFitment: {
        year: vehicle.year,
        make: vehicle.make,
        engine: vehicle.engine,
        body: vehicle.body,
        resolvedMakes: resolveVehicleMakes(vehicle),
        engineForMatch: formatEngineForMatch(vehicle.engine),
      },
      failedChecks: [
        !hasFitmentData ? 'missing_fitment_data' : null,
        hasFitmentData && !yearMatch ? 'year' : null,
        hasFitmentData && !makeMatch ? 'make' : null,
        hasFitmentData && !displacementMatch ? 'engine' : null,
        hasFitmentData && !bodyMatch ? 'body' : null,
      ].filter(Boolean),
    };
  }

  function productMatchesProductTypes(element, productTypes) {
    if (!productTypes || productTypes.length === 0) return true;

    const productType = (element.dataset.productType || '').trim();
    return productTypes.some((type) => type.trim() === productType);
  }

  const YME_PRODUCT_TYPE_ORDER = [
    'All-in-one Kit',
    'Tuner',
    'Tuning',
    'Transmission',
    'ECM',
    'EGR',
    'Delete Pipe',
    'Race Exhaust',
    'Down-Pipe',
    'Exhaust Parts',
    'Turbocharger',
    'Intake Horn',
    'Coolant Filter',
    'Up-Pipes',
    'Fuel pump',
    'Fuel System',
    'Coolant Y-Pipe',
    'Head Studs',
    'Intercooler',
    'Y-Bridge Kit',
    'Manifold Kit',
    'Intake Elbow',
    'Intercooler Pipe',
    'Cold Air Intake',
    'Accessories',
    'Tuning Credit',
    'Gift card',
  ];

  function getProductTypeSortIndex(productType) {
    const index = YME_PRODUCT_TYPE_ORDER.indexOf((productType || '').trim());
    return index === -1 ? YME_PRODUCT_TYPE_ORDER.length : index;
  }

  function sortByProductType(elements) {
    return [...elements].sort((a, b) => {
      const typeCompare =
        getProductTypeSortIndex(a.dataset.productType) - getProductTypeSortIndex(b.dataset.productType);

      if (typeCompare !== 0) return typeCompare;

      return (a.dataset.productTitle || '').localeCompare(b.dataset.productTitle || '');
    });
  }

  window.DdYmeFitment = {
    parseCsvAttribute,
    formatEngineForMatch,
    resolveVehicleMakes,
    productHasFitmentData,
    productMatchesVehicle,
    productMatchesVehicleDebug,
    productMatchesProductTypes,
    sortByProductType,
  };
})();
